import { z } from 'zod';

// Base schemas for reusability
const CustomerBaseSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  language: z.string().min(1, 'Language is required'),
  currencyCode: z.string().min(1, 'Currency is required'),
  phone: z.string().optional(),
  phoneExtension: z.string().optional(),
});

// Core address validation rules (DRY principle)
const AddressValidationRules = {
  streetRequired: z.string().min(1, 'Street address is required'),
  streetOptional: z.string().optional(),
  cityRequired: z.string().min(1, 'City is required'),
  regionOptional: z.string().optional(),
  postalOptional: z.string().optional(),
  countryRequired: z
    .string()
    .min(2, 'Country code is required (e.g. "CA", "US")')
    .max(2, 'Country code should be 2 letters'),
} as const;

// GraphQL-compatible address schema (flat structure)
const GraphQLAddressSchema = z.object({
  street1: AddressValidationRules.streetRequired,
  street2: AddressValidationRules.streetOptional,
  city: AddressValidationRules.cityRequired,
  regionCode: AddressValidationRules.regionOptional,
  postalCode: AddressValidationRules.postalOptional,
  countryCode: AddressValidationRules.countryRequired,
});

// Form-compatible address schema (nested structure for AddressForm)
const FormAddressSchema = z.object({
  address1: AddressValidationRules.streetRequired,
  address2: AddressValidationRules.streetOptional,
  city: AddressValidationRules.cityRequired,
  province: AddressValidationRules.regionOptional,
  postalCode: AddressValidationRules.postalOptional,
  countryCode: AddressValidationRules.countryRequired,
});

// Schema that closely mirrors the GraphQL OrderlessClaimRequestInput structure
export const OrderlessGraphQLInputSchema =
  CustomerBaseSchema.merge(GraphQLAddressSchema);

// Form schema for react-hook-form (for backward compatibility with AddressForm component)
export const OrderlessCustomerFormSchema = CustomerBaseSchema.merge(
  z.object({
    address: FormAddressSchema,
  }),
);

export type OrderlessCustomerData = {
  orderlessCustomer: z.infer<typeof OrderlessCustomerFormSchema>;
};

export type OrderlessGraphQLInputData = z.infer<
  typeof OrderlessGraphQLInputSchema
>;
